import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { Params, RouterModule } from '@angular/router';
import { Product } from '@app/akita/api/products/models/product.model';
import { AkitaCartQuery } from '@app/akita/cart/state/cart.query';
import { AkitaCartService } from '@app/akita/cart/state/cart.service';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaRouterService } from '@app/akita/router/state/router.service';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { logShowAddToCart } from '@app/core/services/google-analytics.lazy';
import { GoogleAnalyticsService } from '@app/core/services/google-analytics.service';
import { map, Observable, Subject, Subscription, takeUntil } from 'rxjs';

import { CommonModule } from '@angular/common';

import { TranslocoModule } from '@ngneat/transloco';
// eslint-disable-next-line max-len
import { AkitaCheckoutService } from '@app/akita/api/checkout/state/checkout.service';
import { InsuranceOverlayDynamicService } from '@app/overlays/insurance-overlay/services/insurance-overlay-dynamic.service';
import { AddToCartButtonComponent } from '@app/shared/components/add-to-cart-button/add-to-cart-button.component';
import { AkitaProductsService } from '@app/akita/api/products/state/products.service';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';
import { Insurance } from '@app/akita/api/products/models/insurance.model';

@Component({
  selector: 'app-featured-buy-buttons',
  templateUrl: './featured-buy-buttons.template.html',
  styleUrls: ['./featured-buy-buttons.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslocoModule, RouterModule, AddToCartButtonComponent],
})
export class FeaturedBuyButtonsComponent implements OnChanges, OnDestroy {
  @Input() public product?: Product | null;
  @Input() public showBuyNow?: boolean | null = false;

  public readonly biggerThan$: Observable<ScreenSizeInfoState>;

  public readonly queryParamsToKeep$: Observable<Params>;

  public trackDeliveryUrlFragment$: Observable<Array<string>>;

  public newDeliveryUrlFragment$: Observable<Array<string>>;

  public showingInsuranceOverlay: boolean;
  private openInsuranceOverlaySubscription?: Subscription | null;

  private readonly onBuySubscription: Subscription;
  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly analytics: GoogleAnalyticsService,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly akitaCartService: AkitaCartService,
    private readonly akitaCartQuery: AkitaCartQuery,
    private readonly akitaRouterService: AkitaRouterService,
    private readonly akitaCheckoutService: AkitaCheckoutService,
    private readonly akitaProductsService: AkitaProductsService,
    private readonly akitaProductsQuery: AkitaProductsQuery,
    private readonly insuranceOverlayDynamicService: InsuranceOverlayDynamicService
  ) {
    this.showingInsuranceOverlay = false;

    this.biggerThan$ = this.akitaScreenQuery.selectBiggerThan();
    this.onBuySubscription = new Subscription();
    this.destroySubject = new Subject();

    this.newDeliveryUrlFragment$ = this.akitaRouterQuery.selectDeliveryUrlFragment('new');
    this.trackDeliveryUrlFragment$ = this.akitaRouterQuery.selectHomeUrlFragment();
    this.queryParamsToKeep$ = this.akitaRouterQuery.selectQueryParamsToKeep();
  }
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.product) {
      this.newDeliveryUrlFragment$ = this.akitaRouterQuery
        .selectDeliveryUrlFragment('new')
        .pipe(
          map((deliveryUrlFragment: Array<string>) => {
            const fragment = [...deliveryUrlFragment];
            if (this.product?.id) {
              fragment.push(this.product.id);
            }
            return fragment;
          })
        );

      this.changeDetector.markForCheck();
    }
  }

  public onAddToCartShowingInsurancePromo(): boolean {
    if (this.openInsuranceOverlaySubscription) {
      this.openInsuranceOverlaySubscription.unsubscribe();
    }
    const language = this.akitaProductsQuery.validateLocale().language;

    if (
      this.product &&
      this.product.insuranceIds &&
      this.product.insuranceIds.length > 0
    ) {
      this.onBuySubscription.add(
        this.akitaProductsService
          .fetchProductInsurances(this.product, language)
          .subscribe({
            next: (insurances: Array<Insurance> | null) => {
              if (this.product && insurances && insurances.length > 0) {
                this.product.insurances = insurances;
                this.openInsuranceOverlaySubscription =
                  this.insuranceOverlayDynamicService.observe
                    .pipe(takeUntil(this.destroySubject))
                    .subscribe({
                      next: (data?: any | null) => {
                        const isOpen = Boolean(data !== null);
                        this.changeDetector.markForCheck();

                        if (
                          !isOpen &&
                          this.insuranceOverlayDynamicService.selectedInsurance &&
                          this.product
                        ) {
                          this.onBuySubscription.add(
                            this.akitaCartService
                              .addProductToCleanCart(
                                this.product,

                                this.insuranceOverlayDynamicService.selectedInsurance
                              )
                              .pipe(takeUntil(this.destroySubject))
                              .subscribe({
                                next: () => {
                                  const sessionId = this.akitaCartQuery.getSessionId();
                                  if (sessionId) {
                                    const fragment =
                                      this.akitaRouterQuery.getCheckoutUrlFragment(
                                        sessionId
                                      );
                                    this.akitaRouterService.navigate(fragment);
                                  } else {
                                    this.onBuySubscription.add(
                                      this.akitaCheckoutService
                                        .createSession()
                                        .pipe(takeUntil(this.destroySubject))
                                        .subscribe({
                                          next: (session) => {
                                            if (session) {
                                              const fragment =
                                                this.akitaRouterQuery.getCheckoutUrlFragment(
                                                  session.id
                                                );
                                              this.akitaRouterService.navigate(fragment);
                                            }
                                          },
                                        })
                                    );
                                  }
                                },
                              })
                          );
                        }
                      },
                    });
                this.insuranceOverlayDynamicService.open({
                  data: {
                    product: this.product,
                  },

                  isBrowser: this.akitaRouterQuery.isBrowser,
                });
              } else {
                this.onGoToCheckout();
              }
            },
            error: () => {},
          })
      );
    } else {
      this.onGoToCheckout();
    }

    return false;
  }

  public onGoToCheckout(): boolean {
    if (this.product?.id) {
      this.onBuySubscription.add(
        this.akitaCartService
          .addProductToCleanCart(this.product, this.product.selectedInsurance)
          .pipe(takeUntil(this.destroySubject))
          .subscribe({
            next: () => {
              const sessionId = this.akitaCartQuery.getSessionId();
              if (sessionId) {
                const fragment = this.akitaRouterQuery.getCheckoutUrlFragment(sessionId);
                this.akitaRouterService.navigate(fragment);
              } else {
                this.onBuySubscription.add(
                  this.akitaCheckoutService
                    .createSession()
                    .pipe(takeUntil(this.destroySubject))
                    .subscribe({
                      next: (session) => {
                        if (session) {
                          const fragment = this.akitaRouterQuery.getCheckoutUrlFragment(
                            session.id
                          );
                          this.akitaRouterService.navigate(fragment);
                        }
                      },
                    })
                );
              }
            },
          })
      );
    }
    return false;
  }

  public onBuyNowButtonLoaded(): void {
    logShowAddToCart(this.analytics.logEventWrapper, 'detail');
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.onBuySubscription) {
      this.onBuySubscription.unsubscribe();
    }
  }
}
